'use client'

import type { StyleWithCssVariables } from '@repo/ui/lib/css-variables'

import { cn } from '@repo/ui/lib/utils'
import { CircleCheck, CircleX, Info, TriangleAlert, XIcon } from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'
import { Toaster as Sonner, ToasterProps } from 'sonner'

// Toasts hug their content up to this width, then wrap. Inheriting the font stops Sonner's own
// font stack overriding the app's.
const toasterStyle: StyleWithCssVariables = { '--width': '440px', fontFamily: 'inherit' }

// The toast inverts the app theme (the `--toast*` tokens in theme.css): dark on the Light theme,
// white on the Dark theme, and red for errors in both. `unstyled` drops Sonner's own look, so the
// toast hides the content of collapsed toasts behind the front one itself. Icons punch their inner
// strokes out in the toast colour, and Sonner's spinner bars (`--gray11`) take the text colour.
const filledIconClassName = 'size-4 fill-current [&>:not(:first-child)]:stroke-(--toast-bg)'

const icons: ToasterProps['icons'] = {
  close: (
    <span className="grid size-6 place-items-center rounded-md group-hover/close:bg-current/15 group-focus-visible/close:outline-2 group-focus-visible/close:outline-current group-active/close:bg-current/25">
      <XIcon className="size-4" />
    </span>
  ),
  error: <CircleX className="size-4" />,
  info: <Info className={filledIconClassName} />,
  success: <CircleCheck className={filledIconClassName} />,
  warning: <TriangleAlert className={filledIconClassName} />,
}

/** The pill style of a toast action, for an action rendered inside a toast's description. */
export const toastActionClassName =
  'inline-flex h-6 shrink-0 cursor-pointer items-center rounded-md bg-(--toast-fg) px-2 text-xs font-medium text-(--toast-bg) outline-none hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--toast-fg) active:opacity-80'

/** An underlined action phrase inside a toast's description. */
export const toastPhraseClassName =
  'cursor-pointer rounded-sm text-(--toast-fg) underline underline-offset-2 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--toast-fg) disabled:cursor-default'

/**
 * A toast phrase that copies on click, then reads "Copied" for 2 seconds. "Copied" fades in over
 * the label in the same grid cell, so the toast keeps its width and the text after it stays put.
 * `onCopy` resolves whether it copied. `busy` shows the phrase as busy while it can't copy yet.
 */
export const ToastCopyPhrase = ({
  busy = false,
  children,
  onCopy,
}: {
  busy?: boolean
  children: ReactNode
  onCopy: () => Promise<boolean>
}) => {
  const [state, setState] = useState<'copied' | 'copying' | 'idle'>('idle')
  useEffect(() => {
    if (state !== 'copied') return
    const timer = setTimeout(() => setState('idle'), 2000)
    return () => clearTimeout(timer)
  }, [state])

  // Ignores clicks while busy instead of disabling, which would drop keyboard focus.
  const isBusy = busy || state === 'copying'
  const copy = () => {
    if (isBusy) return
    setState('copying')
    void onCopy().then(
      copied => setState(copied ? 'copied' : 'idle'),
      () => setState('idle'),
    )
  }
  const copied = state === 'copied'

  return (
    <button
      aria-busy={isBusy}
      className={cn(
        toastPhraseClassName,
        'inline-grid no-underline aria-busy:cursor-progress aria-busy:opacity-60',
      )}
      onClick={copy}
      type="button"
    data-sentry-component="ToastCopyPhrase" data-sentry-source-file="sonner.tsx">
      <span
        aria-hidden={copied}
        className={cn('underline transition-opacity [grid-area:1/1]', copied && 'opacity-0')}
      >
        {children}
      </span>
      <span
        aria-hidden={!copied}
        className={cn('transition-opacity [grid-area:1/1]', !copied && 'opacity-0')}
      >
        Copied
      </span>
    </button>
  )
}

const toastOptions: ToasterProps['toastOptions'] = {
  classNames: {
    actionButton: toastActionClassName,
    closeButton:
      'group/close order-last -my-1 flex cursor-pointer items-center self-stretch border-l border-(--toast-divider) pl-1.5 outline-none',
    content: 'flex min-w-0 flex-col gap-0.5',
    description: 'text-(--toast-muted)',
    icon: 'relative flex size-4 shrink-0 items-center justify-center',
    title: 'font-medium',
    toast:
      'flex w-fit max-w-full items-center gap-2.5 rounded-lg bg-(--toast-bg) py-3 pr-2 pl-3 text-sm text-(--toast-fg) shadow-md [--gray11:var(--toast-fg)] [--toast-bg:var(--toast)] [--toast-divider:var(--toast-border)] [--toast-fg:var(--toast-foreground)] [--toast-muted:var(--toast-muted-foreground)] *:transition-opacity data-[expanded=false]:data-[front=false]:*:opacity-0 data-[type=error]:[--toast-bg:var(--toast-destructive)] data-[type=error]:[--toast-divider:var(--toast-destructive-foreground)] data-[type=error]:[--toast-fg:var(--toast-destructive-foreground)] data-[type=error]:[--toast-muted:color-mix(in_oklab,var(--toast-destructive-foreground)_90%,transparent)]',
  },
  unstyled: true,
}

// Toasts sit left of a docked right panel, which sets `--right-panel-width` on the root, so they
// never cover it, but stay on screen when the window is too narrow for both.
const toasterOffset: ToasterProps['offset'] = {
  right: 'min(calc(var(--right-panel-width, 0px) + 24px), calc(100vw - var(--width) - 24px))',
}

// The `--toast*` tokens follow the app theme, so Sonner keeps its default light theme: its dark
// theme would recolour descriptions for a dark toast, which on the Dark theme is white.
const Toaster = ({ ...props }: ToasterProps) => (
  <Sonner
    className="toaster group"
    closeButton
    icons={icons}
    offset={toasterOffset}
    style={toasterStyle}
    toastOptions={toastOptions}
    {...props}
  data-sentry-element="Sonner" data-sentry-component="Toaster" data-sentry-source-file="sonner.tsx" />
)

export { Toaster }
export { toast } from 'sonner'
